<template>
  <div class="attachments-container">
    <div class="title">
      {{ titleText }}
    </div>
    <table>
      <thead>
        <tr>
          <th>{{ numberFieldName }}</th>
          <th>{{ $t('tender.attachment.document') }}</th>
          <th>{{ $t('summary.awardedDate') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(number, index) in numberArray"
          :key="index"
        >
          <td>
            <a
              v-if="isTender && filterProject(number) && hasReadProjectAccess"
              class="project-link"
              @click="() => navToProject(number)"
            >{{ number }}</a>
            <a
              v-else-if="!isTender && isHKlandUser && maximoProjectHref"
              class="underline"
              :href="maximoProjectHref"
              target="_blank"
            >
              {{ number }}
            </a>
            <span v-else>
              {{ number }}
            </span>
          </td>
          <td>
            <ul
              v-for="file in poFiles[number]"
              :key="file.id"
            >
              <li><a
                class="underline"
                style="word-break: break-all;"
                target="po_attachment"
                @click="downloadFile(file.id)"
              >{{ file.filename }}</a></li>
            </ul>
          </td>
          <td>{{ awardedDate || $t('emptyValue') }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import EventBus from '@/utils/eventBus'
import nav from '@/utils/navigation'
import * as xfs from '@/utils/xfs'
import { aclHelper, aclPermsType, tmsPermsTypes } from '@/utils/aclHelper'
import { getAttachments } from '@/api/docs/request'
import { getLatestProjectByNumber } from '@/api/projects/request'
import { PROJECT_TYPES } from './consts'
import { useMaximoPageUrl } from './hook'
import { useStoreGetters } from '@/store/index'

export default {
  props: {
    purchaseNumbers: [Array, Number, String],
    numberType: [String],
    resourceIdMap: [Object],
    poContracts: Array,
    awardedDate: [String, Date],
    isTender: {
      // When the component was used in a tender detail page
      type: Boolean,
      default: true,
    },
    purchase: {
      type: Object,
      default: () => ({}),
    },
  },
  setup (props) {
    const { isHKlandUser } = useStoreGetters('user', ['isHKlandUser'])
    const { maximoProjectHref } = useMaximoPageUrl(props.purchase, props.numberType)
    return {
      isHKlandUser,
      maximoProjectHref,
    }
  },
  data () {
    return {
      poFiles: {},
      projectMap: {},
    }
  },

  computed: {
    ...mapGetters('user', ['isContractor']),
    ...mapGetters('projects', ['typeLabel']),
    hasReadProjectAccess () {
      return aclHelper.getAccess(aclPermsType.projects.read) || aclHelper.getAccess(tmsPermsTypes.workpermit.read) ||
      aclHelper.getAccess(aclPermsType.projects.manage) || aclHelper.getAccess(aclPermsType.tenders.createUpdate)
    },
    typeLabel () {
      return this.numberType === PROJECT_TYPES.CONTRACT ? this.$t('project.type.contract') : this.$t('project.type.po')
    },
    titleText () {
      return `${this.$t('action.attached')} ${this.typeLabel}`
    },
    numberFieldName () {
      return this.numberType === PROJECT_TYPES.CONTRACT ? this.$t('project.contract.number') : this.$t('project.po.number')
    },
    numberArray () {
      if (Array.isArray(this.purchaseNumbers)) {
        return this.purchaseNumbers
      } else if (this.purchaseNumbers) {
        return [this.purchaseNumbers]
      } else {
        return []
      }
    },
  },

  watch: {
    async resourceIdMap () {
      if (this.purchaseNumbers && this.resourceIdMap) {
        await this.refreshPoFiles()
      }
    },
  },

  async mounted () {
    EventBus.$on('modules_tender_refresh_pofiles', this.refreshPoFiles)
    if (this.purchaseNumbers && this.resourceIdMap) {
      await this.refreshPoFiles()
    }
  },

  destroyed () {
    EventBus.$off('modules_tender_refresh_pofiles')
    this.poFiles = {}
  },
  methods: {
    async downloadFile (fileId) {
      await xfs.downloadFile(`/docs/${fileId}`)
    },
    filterProject (number) {
      // This line is in order to compatible with old contract no distinction between revisions
      let poContract = this.poContracts.find(item => item.number === number)
      if (!poContract) {
        poContract = this.poContracts.find(item => `${item.number}-${item.revisionNum}` === number.toString())
      }
      return poContract ? this.projectMap[poContract.id] : null
    },
    async refreshPoFiles () {
      if (!this.numberArray.length || !this.resourceIdMap) {
        return
      }
      if (this.isTender && this.hasReadProjectAccess) {
        const numberIds = this.poContracts.map(item => item.id)
        this.projectMap = await getLatestProjectByNumber(numberIds, this.numberType)
      }
      for (let poNumber of this.numberArray) {
        if (this.resourceIdMap[poNumber]) {
          const attachments = await getAttachments(this.resourceIdMap[poNumber])
          this.$set(this.poFiles, poNumber.toString(), attachments)
        }
      }
    },
    navToProject (poNumber) {
      const projectId = this.filterProject(poNumber)
      if (projectId) {
        nav.toPage(this.isContractor ? 'vendorProjectDetail' : 'projectDetail', { id: projectId })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/table';

.attachments-container {
  margin-top: 20px;
  padding-top: 14px;
  padding-bottom: 14px;
  & > .title {
    padding-left: 20px;
  }
}

.title {
  font-family: Verdana;
  font-size: 16px;
  text-align: left;
  color: #0c3a58;
  padding-bottom: .5rem;
}

.project-link {
  text-decoration: underline;
}

ul {
  margin-left: 0;
}
li {
  list-style-type: none;
}
</style>
